import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { ArrowUpRightIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, TriangleAlertIcon } from 'lucide-react'

import { Button, Card } from '@repo/ui'
import { EmptyState } from 'components'
import { headerWithTerm } from 'components/display/Table/headerWithTerm'
import { INFO_HEADER_ALLOWANCE } from 'components/display/Table/headerWithInfo'
import { Table } from 'components/display/Table/Table'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useCanAccess, useReferrerState } from 'hooks'
import { useSeoPageMeta, useWebsitePages } from 'hooks/api'
import { ROUTES } from 'routes/routes'
import type { DayRange, PageMetaRow } from 'services'

import { DESTINATION_OPTIONS } from '../../TrackedLinks/destinations'
import { AUDIENCE_LABELS, formatCount, formatDuration, formatPct, sourceNotice } from '../websiteAnalyticsModel'
import { classifyPages, otherSummary, publishedTitles, type PageRow } from '../websitePagesModel'
import { normalizePath, pageMetaDeepLink } from './seo/seoPageMetaModel'
import { Notice, SectionNote, useTone } from './shared'

/**
 * VRG-690 (Spencer, 2026-10-13) — Page analysis: which vera.inc pages are visited most in the
 * range. A SECTION of the page, not the page, so the shared Table runs embedded: no action
 * line, no footer, a fixed height and no `fillBottom` (§22).
 *
 * Two populations, side by side and labelled (task Decisions; same rule as the rest of the
 * page): Visitors and Page views are Vercel's (everyone); Avg time, Entries and Exit
 * rate are PostHog's (visitors with analytics on), a SAMPLE, so they are shapes and shares,
 * not totals. No figure mixes the two.
 *
 * Published pages (vera-website PUBLIC_ROUTES, mirrored in TrackedLinks/destinations.ts) are
 * listed; everything else — staged pages, old paths, invite links, Vercel's "Others" tail —
 * folds into one pinned "Other paths" row, expandable.
 */

const TITLES = publishedTitles(DESTINATION_OPTIONS)
// Measured at 1280 (the grid's own two-line row and its header), so the table is exactly as
// tall as its rows up to MAX_BODY_ROWS, and scrolls inside itself past that.
const ROW_HEIGHT = 72
const HEADER_HEIGHT = 44
const MAX_BODY_ROWS = 8

const num = (v: number | null) => (v === null ? -1 : v)

/*
  Widths are sized for the HEADER (§49), measured at 1280: the label at 500 14px Lato plus
  ~63px of header chrome (20px padding, the 28px sort button, the title container's own gap)
  plus the info glyph where there is one (INFO_HEADER_ALLOWANCE). Six columns then fit the
  891px card body with the Page column at ~220px, so nothing scrolls sideways (§74).

  ⚠️ A seventh column, Leads (Get started forms per page), was built and measured: it left
  the Page column ~110px, too narrow for a page name. The API still returns `leads` per row;
  per-page form submissions stay on Sources → Page, where they already were.

  VRG-695 added one flex column, "In search", for viewers holding `internal_gtm_view`: Page
  and In search share the flex space with a 150px floor each. Measured 2026-10-13: 215px each
  at 1280, 191px each at 1440, and no horizontal scroll at either. Without the grant the
  column is absent and Page keeps its 200px floor.
*/

const PageCell = ({ row }: { row: PageRow }) => (
  <Stack sx={{ minWidth: 0, py: 1 }}>
    <Typography variant="body3" noWrap sx={{ fontWeight: row.path === '__other__' ? 600 : 400 }}>
      {row.title ?? row.path}
    </Typography>
    {row.path !== '__other__' && (row.title || !row.published) ? (
      <Typography variant="body4" color="text.secondary" noWrap>
        {row.published ? row.path : row.title ? `${row.path} · not in the sitemap` : 'Not in the sitemap'}
      </Typography>
    ) : null}
  </Stack>
)

/*
  VRG-695 — how the page appears in search, read from the SEO page's live page-meta fetch
  (`useSeoPageMeta`, the API's 5-minute cache): no second fetch path. Only rendered for a
  viewer holding `internal_gtm_view`, the grant that gates that data; Analytics' own
  `internal_analytics_view` never reaches it.
*/
const SearchAppearanceCell = ({ row, meta }: { row: PageRow; meta: Map<string, PageMetaRow> | null }) => {
  const tone = useTone()
  const theme = useTheme()
  const referrer = useReferrerState('Website')
  if (row.path === '__other__') return null
  if (!meta) return <Typography variant="body4" color="text.secondary">…</Typography>
  const m = meta.get(normalizePath(row.path))
  if (!m)
    return (
      <Typography variant="body4" color="text.secondary" noWrap>
        Not in sitemap
      </Typography>
    )
  const broken = !!(m.flags.title || m.flags.description)
  const tip = (
    <Stack spacing={1}>
      <strong>{m.title ?? 'No <title>'}</strong>
      <span>{m.titleChars === null ? '' : `${m.titleChars} characters`}</span>
      <span>{m.description ?? 'No meta description'}</span>
      <span>{m.descriptionChars === null ? '' : `${m.descriptionChars} characters`}</span>
      {broken ? <span>Outside the website&rsquo;s limits (title ≤ 60, description 70–160).</span> : null}
    </Stack>
  )
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, width: '100%' }}>
      {broken ? (
        <Box component="span" sx={{ display: 'inline-flex', color: tone('caution').color, flexShrink: 0 }} aria-label="Breaks the length limits">
          <TriangleAlertIcon size={14} strokeWidth={1.75} />
        </Box>
      ) : null}
      <InfoTooltip text={tip}>
        <Typography variant="body3" noWrap sx={{ minWidth: 0, flex: 1, cursor: 'help' }}>
          {m.title ?? (m.error ? 'Could not read' : '—')}
        </Typography>
      </InfoTooltip>
      <Box
        component={RouterLink}
        to={pageMetaDeepLink(ROUTES.INTERNAL_GTM_SEO, row.path)}
        state={referrer}
        aria-label={`Open ${row.path} in Page titles & link previews`}
        title="Open in Page titles & link previews"
        sx={{ display: 'inline-flex', color: 'text.secondary', flexShrink: 0, '&:hover': { color: theme.palette.brand[500] } }}
      >
        <ArrowUpRightIcon size={14} strokeWidth={1.5} />
      </Box>
    </Stack>
  )
}

const buildColumns = (meta: Map<string, PageMetaRow> | null, withSearch: boolean) => [
  {
    field: 'page',
    headerName: 'Page',
    flex: 1,
    minWidth: withSearch ? 150 : 200,
    valueGetter: (_v: unknown, row: PageRow) => row.title ?? row.path,
    renderCell: ({ row }: { row: PageRow }) => <PageCell row={row} />,
  },
  ...(withSearch
    ? [
        {
          field: 'searchAppearance',
          headerName: 'In search',
          flex: 1,
          minWidth: 150,
          sortable: false,
          renderHeader: headerWithTerm('In search', 'seo-page-title'),
          renderCell: ({ row }: { row: PageRow }) => <SearchAppearanceCell row={row} meta={meta} />,
        },
      ]
    : []),
  {
    field: 'visitors',
    headerName: 'Visitors',
    width: 114,
    type: 'number',
    valueGetter: (_v: unknown, row: PageRow) => num(row.visitors),
    renderCell: ({ row }: { row: PageRow }) => formatCount(row.visitors),
  },
  {
    field: 'pageviews',
    headerName: 'Page views',
    width: 134 + INFO_HEADER_ALLOWANCE,
    type: 'number',
    renderHeader: headerWithTerm('Page views', 'website-page-views'),
    valueGetter: (_v: unknown, row: PageRow) => num(row.pageviews),
    renderCell: ({ row }: { row: PageRow }) => formatCount(row.pageviews),
  },
  {
    field: 'avgSeconds',
    headerName: 'Avg time',
    width: 120 + INFO_HEADER_ALLOWANCE,
    type: 'number',
    renderHeader: headerWithTerm('Avg time', 'website-time-on-page'),
    valueGetter: (_v: unknown, row: PageRow) => num(row.avgSeconds),
    renderCell: ({ row }: { row: PageRow }) => formatDuration(row.avgSeconds),
  },
  {
    field: 'entries',
    headerName: 'Entries',
    width: 110 + INFO_HEADER_ALLOWANCE,
    type: 'number',
    renderHeader: headerWithTerm('Entries', 'website-page-entries'),
    valueGetter: (_v: unknown, row: PageRow) => num(row.entries),
    renderCell: ({ row }: { row: PageRow }) => formatCount(row.entries),
  },
  {
    field: 'exitRate',
    headerName: 'Exit rate',
    width: 118 + INFO_HEADER_ALLOWANCE,
    type: 'number',
    renderHeader: headerWithTerm('Exit rate', 'website-exit-rate'),
    valueGetter: (_v: unknown, row: PageRow) => num(row.exitRate),
    renderCell: ({ row }: { row: PageRow }) => (
      <span title={row.exits === null ? undefined : `${formatCount(row.exits)} exits of ${formatCount(row.samplePageviews)} views`}>
        {formatPct(row.exitRate)}
      </span>
    ),
  },
]

export const WebsitePagesCard = ({ range }: { range: DayRange }) => {
  const { data, isLoading, error } = useWebsitePages(range)
  const [showOther, setShowOther] = useState(false)
  const { isHasAccess: canSeeSeo } = useCanAccess({ action: 'internal_gtm_view' })
  const pageMeta = useSeoPageMeta(canSeeSeo)
  const meta = useMemo(
    () => (pageMeta.data ? new Map(pageMeta.data.rows.map((r) => [normalizePath(r.route), r])) : null),
    [pageMeta.data],
  )
  const columns = useMemo(() => buildColumns(meta, canSeeSeo), [meta, canSeeSeo])

  const { published, other } = useMemo(() => classifyPages(data?.rows ?? [], TITLES), [data])
  const summary = useMemo(() => (other.length ? otherSummary(other) : null), [other])
  const rows = showOther ? [...published, ...other] : published
  const pinned = !showOther && summary ? { bottom: [summary] } : undefined
  const bodyRows = Math.max(3, Math.min(MAX_BODY_ROWS, rows.length + (pinned ? 1 : 0)))
  const height = HEADER_HEIGHT + ROW_HEIGHT * bodyRows + 2

  const posthogProblem =
    data && (data.sources.posthog.status === 'error' || data.sources.posthog.status === 'not_configured')
      ? sourceNotice(AUDIENCE_LABELS.consented, data.sources.posthog)
      : null

  return (
    <Card
      title="Pages"
      action={
        <Stack direction="row" spacing={3} alignItems="center">
          <InfoTooltip term="website-page-analysis" />
          {other.length ? (
            <Button
              variant="secondary"
              onClick={() => setShowOther((v) => !v)}
              startIcon={showOther ? <ChevronUpIcon strokeWidth={1} /> : <ChevronDownIcon strokeWidth={1} />}
            >
              {showOther ? 'Hide other paths' : `Show other paths (${other.length})`}
            </Button>
          ) : null}
        </Stack>
      }
    >
      <Stack spacing={3} sx={{ mt: 4 }} data-testid="website-pages-card">
        <SectionNote>
          Visitors and page views: {AUDIENCE_LABELS.all}. Avg time, entries and exit rate:{' '}
          {AUDIENCE_LABELS.consented}, a sample, so read them as shares and shapes rather than totals.
          {data?.vercelOrigin === 'snapshot'
            ? ' This range reaches past Vercel’s reporting window, so visitors are summed from daily snapshots and can count a person once per day.'
            : ''}
        </SectionNote>
        {posthogProblem ? <Notice severity={data?.sources.posthog.status === 'error' ? 'error' : 'warning'}>{posthogProblem}</Notice> : null}
        {error ? (
          <Notice severity="error">Could not load the page analysis. Reload the page, or check the API is running.</Notice>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            pinnedRows={pinned}
            getRowId={(row: PageRow) => row.path}
            loading={isLoading}
            defaultSortModel={[{ field: 'pageviews', sort: 'desc' }]}
            showActionLine={false}
            showSelection={false}
            showFooter={false}
            hideExport
            height={height}
            emptyState={
              <EmptyState
                title="No page views in this range"
                text="Once vera.inc has visitors in the range, its pages are ranked here."
                icon={<FileTextIcon strokeWidth={1} size={32} />}
              />
            }
          />
        )}
      </Stack>
    </Card>
  )
}
